/**
 * The login app's one loader. Reused by the sign-in, activation and self-onboarding
 * pages so every busy state — button, full-page redirect, inline fetch — looks and
 * behaves identically. Pure DOM + CSS (the ".spinner" / ".loading-overlay" block in
 * styles.css); no dependencies, so it adds nothing to the bundle at load.
 *
 * # ui-no-screenshot-needed: additive shared UI; the pages that consume it carry the
 * visual smoke against the served app.
 */

/**
 * A teal ring spinner element. It is `aria-hidden` on purpose — a CSS animation
 * carries no meaning to a screen reader, so the caller owns the accessible status
 * text (button label swap, aria-busy, or an aria-live region).
 */
export function createSpinner(size: "sm" | "lg" = "sm"): HTMLSpanElement {
  const spinner = document.createElement("span");
  spinner.className = size === "lg" ? "spinner spinner--lg" : "spinner";
  spinner.setAttribute("aria-hidden", "true");
  return spinner;
}

interface ButtonBusyOptions {
  /** Label shown while the action is in flight, e.g. "Signing in…". */
  busyLabel: string;
  /** Label restored once the action settles, e.g. "Sign In". */
  idleLabel: string;
  /** Owning form to flag with aria-busy, so the state is heard and not only seen. */
  form?: HTMLFormElement | null;
}

/**
 * Toggle a submit button between idle and busy: disabled, a leading spinner glyph,
 * and a label swap — plus aria-busy on the owning form. Replaces the three
 * near-identical `setBusy()` helpers the login pages had each grown separately.
 */
export function setButtonBusy(
  button: HTMLButtonElement,
  busy: boolean,
  options: ButtonBusyOptions,
): void {
  button.disabled = busy;
  button.classList.toggle("is-busy", busy);
  if (busy) {
    button.replaceChildren(createSpinner(), document.createTextNode(options.busyLabel));
  } else {
    button.replaceChildren(document.createTextNode(options.idleLabel));
  }
  options.form?.setAttribute("aria-busy", String(busy));
}

/**
 * Cover the whole viewport with a branded loader once sign-in has SUCCEEDED, and keep it up through
 * the cross-SPA `window.location.assign()` that hands off to a portal. Called only on the success
 * path (see main.ts), never during validation — so a rejected password shows the button's
 * "Signing in…" spinner and its error, not this overlay.
 *
 * # ui-no-screenshot-needed: copy/behaviour of the shared loader; unifies the login loader with
 * every portal's boot loader so the post-sign-in wait is one continuous loader.
 *
 * The message is "Getting your workspace ready…" — the SAME copy, spinner and ground every portal's boot
 * loader uses — so from the successful sign-in, through the redirect, until the portal paints its
 * dashboard, the person sees ONE continuous loader, not a sequence of different ones. Idempotent —
 * a second call is a no-op.
 */
export function showRedirectOverlay(message = "Getting your workspace ready…"): void {
  if (document.getElementById("app-loading-overlay") !== null) return;

  const overlay = document.createElement("div");
  overlay.id = "app-loading-overlay";
  overlay.className = "loading-overlay";
  overlay.setAttribute("role", "status");
  overlay.setAttribute("aria-live", "polite");

  const label = document.createElement("p");
  label.className = "loading-overlay__label";
  label.textContent = message;

  overlay.append(createSpinner("lg"), label);
  document.body.appendChild(overlay);
}
